import * as THREE from "three";
import { dayNight } from "../dayNight.js";

/** Cat names by palette (aligned to PALS below): gray tabby = Boba, ginger = Jack, black = Lucky. */
const NAME_BY_PAL = ["Boba", "Jack", "Lucky"];

/**
 * Ambient pixel CATS that wander a zone. Each cat is a base-anchored billboard paging through a
 * procedurally-drawn 32px sprite sheet with three animation states:
 *   • WALK  — a 4-frame trot, directional (front / back / side, side mirrored for the other facing)
 *   • IDLE  — a 2-frame breathing + tail-flick while standing, directional
 *   • SLEEP — a 2-frame curled-up nap (non-directional) with drifting "z"s; cats nod off now + then
 * The view is picked from the cat's heading RELATIVE to the camera (same trick as the player).
 */

const CELL = 32;
const COLS = 6, ROWS = 5;            // cols: walk 0-3, idle 4-5 ; rows: down,up,sideR,sideL,sleep
const WALK = [0, 1, 2, 3], IDLE = [4, 5], SLEEP = [0, 1];
const WALK_MS = 120, IDLE_MS = 520, SLEEP_MS = 750;
const SPEED = 1.1;                   // world units / sec
const SLEEP_CHANCE = 0.32;           // odds a pause turns into a nap

/** 8 facing rows (Down, DownR, Right, UpR, Up, UpL, Left, DownL) → sheet view row (0 down,1 up,2 R,3 L). */
const VIEW_BY_ROW = [0, 2, 2, 2, 1, 3, 3, 3];

interface CatPal { body: string; dark: string; light: string; nose: string; eye: string }
const PALS: CatPal[] = [
  { body: "#8a8f98", dark: "#565a62", light: "#bcc1c9", nose: "#d98a9a", eye: "#1c1c20" }, // gray tabby
  { body: "#caa15a", dark: "#9a7836", light: "#e8cda4", nose: "#d98a9a", eye: "#1c1c20" }, // ginger
  { body: "#2c2c32", dark: "#161618", light: "#474750", nose: "#d98a9a", eye: "#cfe06a" }, // black, green eyes
];

/** A directional cat (view 0=down,1=up,2=side-right). `leg` shifts the legs; `tailUp`/`blink` for idle. */
function drawDir(ctx: CanvasRenderingContext2D, ox: number, oy: number, view: number, leg: number, tailUp: boolean, blink: boolean, p: CatPal): void {
  const r = (x: number, y: number, w: number, h: number, c: string): void => { ctx.fillStyle = c; ctx.fillRect(ox + x, oy + y, w, h); };
  const a = leg === 1 ? 1 : 0, b = leg === 3 ? 1 : 0; // alternating leg dips for the 4-frame trot
  if (view === 0) {                         // DOWN — facing viewer
    r(8, 3, 4, 5, p.body); r(20, 3, 4, 5, p.body);                 // ears
    r(9, 5, 2, 2, p.dark); r(21, 5, 2, 2, p.dark);
    r(7, 7, 18, 11, p.body); r(11, 13, 10, 4, p.light);           // head + muzzle
    if (blink) { r(11, 11, 3, 1, p.dark); r(18, 11, 3, 1, p.dark); }
    else { r(11, 10, 3, 3, p.eye); r(18, 10, 3, 3, p.eye); r(11, 10, 1, 1, p.light); r(18, 10, 1, 1, p.light); }
    r(15, 14, 2, 2, p.nose);
    r(9, 17, 14, 10, p.body); r(12, 18, 8, 6, p.light);           // body + chest
    r(11, 19, 1, 6, p.dark); r(20, 19, 1, 6, p.dark);             // stripes
    r(10, 26, 4, 4 + a, p.body); r(18, 26, 4, 4 + b, p.body);     // front legs
    if (tailUp) { r(23, 11, 3, 4, p.body); r(24, 8, 3, 4, p.body); } else { r(23, 18, 4, 3, p.body); r(25, 15, 3, 5, p.body); }
  } else if (view === 1) {                  // UP — facing away
    r(8, 3, 4, 5, p.body); r(20, 3, 4, 5, p.body);
    r(9, 5, 2, 2, p.dark); r(21, 5, 2, 2, p.dark);
    r(7, 7, 18, 11, p.body);
    r(9, 17, 14, 10, p.body);
    r(13, 8, 1, 18, p.dark); r(18, 12, 1, 13, p.dark);            // back stripes
    r(10, 26, 4, 4 + a, p.body); r(18, 26, 4, 4 + b, p.body);
    r(15, tailUp ? 0 : 2, 2, 8, p.body);                         // tail straight up (flicks in idle)
  } else {                                  // SIDE (right) — profile
    r(4, 13, 18, 8, p.body); r(6, 14, 13, 3, p.light);            // body
    r(8, 14, 1, 6, p.dark); r(12, 14, 1, 6, p.dark); r(16, 14, 1, 6, p.dark); // stripes
    r(20, 8, 8, 9, p.body);                                       // head (right)
    r(21, 5, 4, 4, p.body); r(22, 6, 2, 2, p.dark);              // ear
    if (blink) r(24, 11, 2, 1, p.dark); else { r(24, 11, 2, 2, p.eye); r(24, 11, 1, 1, p.light); }
    r(27, 13, 1, 2, p.nose);                                      // nose
    r(19, 20, 3, 4 + a, p.body); r(14, 20, 3, 4 + b, p.body);     // legs (trot)
    r(9, 20, 3, 4 + a, p.body); r(5, 20, 3, 4 + b, p.body);
    if (tailUp) { r(2, 8, 3, 4, p.body); r(1, 5, 3, 4, p.body); } else { r(2, 11, 3, 4, p.body); r(1, 9, 3, 4, p.body); }
  }
}

/** A curled, sleeping cat (non-directional) with drifting "z"s; `f` is the breathing/z frame. */
function drawSleep(ctx: CanvasRenderingContext2D, ox: number, oy: number, f: number, p: CatPal): void {
  const r = (x: number, y: number, w: number, h: number, c: string): void => { ctx.fillStyle = c; ctx.fillRect(ox + x, oy + y, w, h); };
  const lift = f === 1 ? 1 : 0; // gentle breathing rise
  r(6, 19 - lift, 22, 9 + lift, p.body);                          // curled loaf body
  r(4, 22 - lift, 26, 6 + lift, p.body);
  r(8, 21 - lift, 18, 3, p.light);                                // back highlight
  r(11, 22, 1, 4, p.dark); r(16, 22, 1, 4, p.dark); r(21, 22, 1, 4, p.dark); // stripes
  r(5, 18 - lift, 4, 4, p.body); r(6, 16 - lift, 3, 3, p.body);   // ears (tucked, left)
  r(6, 24, 7, 4, p.body);                                         // head resting in front
  r(7, 25, 4, 1, p.dark);                                         // closed eye
  r(28, 24, 4, 3, p.body); r(30, 22, 2, 3, p.body);              // tail wrapped to the right
  // drifting "z"s (sleep)
  ctx.fillStyle = p.dark;
  const z = (zx: number, zy: number, s: number): void => { r(zx, zy, s, 1, p.dark); r(zx + s - 1, zy + 1, 1, 1, p.dark); r(zx, zy + 2, s, 1, p.dark); };
  z(22, 12 - f, 3);
  if (f === 1) z(26, 7, 4);
}

function buildSheet(p: CatPal): HTMLCanvasElement {
  const c = document.createElement("canvas"); c.width = COLS * CELL; c.height = ROWS * CELL;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  for (let view = 0; view < 3; view++) {                          // down(0), up(1), sideR(2)
    for (const col of WALK) drawDir(ctx, col * CELL, view * CELL, view, col, false, false, p);
    drawDir(ctx, IDLE[0] * CELL, view * CELL, view, 0, false, false, p);
    drawDir(ctx, IDLE[1] * CELL, view * CELL, view, 0, true, true, p); // tail flick + slow blink
  }
  for (let col = 0; col < COLS; col++) {                          // side-left = mirror of side-right row
    ctx.save(); ctx.translate(col * CELL + CELL, 3 * CELL); ctx.scale(-1, 1);
    ctx.drawImage(c, col * CELL, 2 * CELL, CELL, CELL, 0, 0, CELL, CELL); ctx.restore();
  }
  for (const col of SLEEP) drawSleep(ctx, col * CELL, 4 * CELL, col, p);
  return c;
}

type State = "walk" | "idle" | "sleep";
interface CatEntity {
  id: string; name: string;
  sprite: THREE.Sprite; tex: THREE.Texture;
  x: number; z: number; tx: number; tz: number;
  heading: number; phase: number; clock: number; pause: number; state: State;
}
type Area = { x0: number; z0: number; x1: number; z1: number };

export class CatController {
  private readonly cats: CatEntity[] = [];
  private readonly base: THREE.CanvasTexture[];
  private visible = false;

  constructor(private readonly scene: THREE.Scene, private readonly area: Area, count = 3) {
    this.base = PALS.map((p) => {
      const t = new THREE.CanvasTexture(buildSheet(p));
      t.magFilter = t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
      return t;
    });
    for (let i = 0; i < count; i++) this.spawn(i);
  }

  private rand(lo: number, hi: number): number { return lo + Math.random() * (hi - lo); }

  private spawn(i: number): void {
    const tex = this.base[i % this.base.length].clone();
    tex.needsUpdate = true; tex.repeat.set(1 / COLS, 1 / ROWS);
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.5 }));
    sprite.center.set(0.5, 0); sprite.scale.set(0.62, 0.62, 1); sprite.visible = false;
    const x = this.rand(this.area.x0, this.area.x1), z = this.rand(this.area.z0, this.area.z1);
    sprite.position.set(x, 0.02, z);
    dayNight.registerMat(sprite.material, sprite.position); // cats darken/cool at night + light up near lamps
    this.scene.add(sprite);
    this.cats.push({ id: `cat${i}`, name: NAME_BY_PAL[i % NAME_BY_PAL.length], sprite, tex, x, z, tx: x, tz: z, heading: 0, phase: 0, clock: 0, pause: this.rand(0.5, 3), state: "idle" });
  }

  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    for (const c of this.cats) c.sprite.visible = v;
  }

  tick(dt: number, camYaw: number): void {
    if (!this.visible) return;
    for (const c of this.cats) {
      if (c.pause > 0) {
        c.pause -= dt;
        if (c.pause <= 0) { c.tx = this.rand(this.area.x0, this.area.x1); c.tz = this.rand(this.area.z0, this.area.z1); c.state = "walk"; c.phase = 0; c.clock = 0; }
      } else {
        const dx = c.tx - c.x, dz = c.tz - c.z, d = Math.hypot(dx, dz);
        if (d < 0.2) { // arrived — rest (and sometimes curl up for a nap)
          const nap = Math.random() < SLEEP_CHANCE;
          c.state = nap ? "sleep" : "idle"; c.pause = nap ? this.rand(7, 16) : this.rand(1.5, 4); c.phase = 0; c.clock = 0;
        } else { const s = Math.min(d, SPEED * dt); c.x += (dx / d) * s; c.z += (dz / d) * s; c.heading = Math.atan2(dx, dz); c.state = "walk"; }
      }
      const cols = c.state === "walk" ? WALK : c.state === "idle" ? IDLE : SLEEP;
      const ms = c.state === "walk" ? WALK_MS : c.state === "idle" ? IDLE_MS : SLEEP_MS;
      c.clock += dt * 1000;
      while (c.clock >= ms) { c.clock -= ms; c.phase = (c.phase + 1) % cols.length; }
      const row = c.state === "sleep" ? 4 : VIEW_BY_ROW[Math.round((((c.heading - camYaw) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) / (Math.PI / 4)) % 8];
      c.tex.offset.set(cols[c.phase] / COLS, 1 - (row + 1) / ROWS);
      c.sprite.position.set(c.x, 0.02, c.z);
    }
  }

  /** Raycast the visible cats; returns the picked cat's id (or null). */
  pick(ray: THREE.Raycaster): string | null {
    const vis = this.cats.filter((c) => c.sprite.visible);
    const obj = ray.intersectObjects(vis.map((c) => c.sprite), false)[0]?.object;
    return obj ? (vis.find((c) => c.sprite === obj)?.id ?? null) : null;
  }

  /** Ground position of the cat with `id` (for the selection ring), or null. */
  posOf(id: string): { x: number; y: number; z: number } | null {
    const c = this.cats.find((c) => c.id === id);
    return c ? { x: c.x, y: 0.02, z: c.z } : null;
  }

  /** The cat's display name (shown when selected), or null. */
  infoOf(id: string): { name: string } | null {
    const c = this.cats.find((c) => c.id === id);
    return c ? { name: c.name } : null;
  }

  dispose(): void {
    for (const c of this.cats) { dayNight.unregisterMat(c.sprite.material); this.scene.remove(c.sprite); c.sprite.material.dispose(); c.tex.dispose(); }
    for (const t of this.base) t.dispose();
    this.cats.length = 0;
  }
}
